/**
 * Where each record type's own page lives, for links from record-agnostic places — the audit
 * trail, notifications, approvals (ERD §8.9). A plain map: a type without a detail page has no
 * entry, and a link to it is simply not offered. The API still decides whether the record may be
 * seen (SDD §22.4).
 */
export const RECORD_PATHS: Readonly<Record<string, string>> = {
  'hr.employee': '/hr/employees/:id',
  'hr.position': '/hr/positions/:id',
  'procurement.supplier': '/suppliers/:id',
  'platform.app_user': '/admin/users/:id',
  'finance.supplier_invoice': '/supplier-invoices/:id',
  'finance.supplier_credit_note': '/supplier-credit-notes/:id',
  'finance.payment': '/payments/:id',
  'finance.bank_cash_transaction': '/bank-cash/movements/:id',
  'finance.customer_invoice': '/customer-invoices/:id',
  'finance.expense_claim': '/expense-claims/:id',
  'finance.customer_credit_note': '/customer-credit-notes/:id',
  'finance.customer_receipt': '/customer-receipts/:id',
  'ledger.accounting_period': '/periods/:id',
  'ledger.journal': '/journals/:id',
  'ledger.account': '/accounts/:id',
  'assets.asset': '/assets/:id',
  'crm.client': '/crm/clients/:id',
  'crm.client_activity': '/crm/activities/:id',
  'crm.client_contact': '/crm/contacts/:id',
  'organization.department': '/organization/departments/:id',
  'organization.cost_center': '/organization/cost-centres/:id',
  'organization.location': '/organization/locations/:id',
  'organization.product_service': '/organization/products-services/:id',
  'projects.project': '/projects/:id',
  'hr.leave_request': '/hr/leave/requests/:id',
  'hr.employee_request': '/hr/requests/:id',
  'hr.leave_cancellation': '/hr/leave/cancellations/:id',
  'marketing.lead': '/marketing/leads/:id',
  'marketing.campaign': '/marketing/campaigns/:id',
  'quality.quality_issue': '/quality/issues/:id',
  'procurement.purchase_request': '/procurement/purchase-requests/:id',
  'procurement.purchase_order': '/procurement/purchase-orders/:id',
  'procurement.goods_receipt': '/procurement/receipts/:id',
  'migration.batch': '/migration/batches/:id',
  // A chat notification or search hit names the conversation (SDD §29.6, §29.8).
  'platform.conversation': '/chat/:id',
  'platform.chat_group': '/admin/chat-groups/:id',
};

/** The page of one record, or null when its type has no page. */
export function recordPath(recordType: string, id: string): string | null {
  const pattern = Object.hasOwn(RECORD_PATHS, recordType) ? RECORD_PATHS[recordType] : undefined;
  return pattern === undefined ? null : pattern.replace(':id', encodeURIComponent(id));
}

/** The view permission a record's page needs, so a link is offered only when it would open. */
export function viewCapability(recordType: string): string {
  return `${recordType}:view`;
}
